import { DateTime } from "luxon";
import React, { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import { IgAccordion, IgPaper, IgTypography } from "@intelligems/ig-theme";
import { GetStripeMeterLogsResponseBodyType } from "@intelligems/ig-types/functions/stripe-meter-logs/get-stripe-meter-logs";
import { Box, Drawer, Grid } from "@mui/material";
import { useGetOrganizationMeterLogs } from "@src/api/organization-meter-logs/get-organization-meter-logs";
import { SkeletonGroup } from "@src/components/Utils/SkeletonGroup";
import { ShopifyChargeTable } from "@src/features/admin/pages/agencies/components/Tables/ShopifyChargeTable/ShopifyChargeTable";
import { StripeEventMeterLogTable } from "@src/features/admin/pages/agencies/components/Tables/StripeEventMeterLogTable/StripeEventMeterLogTable";
import { closeDrawer, getDrawer } from "@src/store/slices/drawers";

interface ContentProps {
  subscriptionLinkId: string;
  organizationId: string;
  stripeProductLinkId: string | undefined;
}
const Content = ({
  subscriptionLinkId,
  organizationId,
  stripeProductLinkId,
}: ContentProps) => {
  const meterLogEvents = useGetOrganizationMeterLogs(
    organizationId,
    subscriptionLinkId
  );

  const data: GetStripeMeterLogsResponseBodyType = useMemo(() => {
    if (meterLogEvents.isSuccess) {
      if (stripeProductLinkId) {
        return {
          stripeEvents: meterLogEvents.data.stripeEvents.map((stripeEvent) => ({
            ...stripeEvent,
            events: stripeEvent.events.filter(
              (e) => e.stripeProductLinkId === stripeProductLinkId
            ),
          })),
          shopifyEvents: meterLogEvents.data.shopifyEvents,
        };
      }
      return meterLogEvents.data;
    }
    return { stripeEvents: [], shopifyEvents: [] };
  }, [meterLogEvents.isSuccess, meterLogEvents.data]);

  if (!meterLogEvents.isSuccess) {
    return (
      <Grid container mt={2}>
        <SkeletonGroup lines={3} />
      </Grid>
    );
  }

  if (!data.shopifyEvents.length && !data.stripeEvents.length) {
    return (
      <Grid
        container
        direction={"column"}
        rowSpacing={1}
        mt={2}
        alignContent={"center"}
      >
        <Grid>
          <IgTypography>No charges found</IgTypography>
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid container direction={"column"} rowSpacing={2} mt={2}>
      {data.stripeEvents.length ? (
        <Grid item container direction={"column"} rowSpacing={2}>
          <Grid item>
            <IgTypography sx={{ fontWeight: 600 }}>Stripe Events</IgTypography>
          </Grid>
          <Grid item container rowSpacing={2} direction={"column"}>
            {data.stripeEvents.map((event, index) => (
              <Grid item xs key={`${event.periodStart}-${event.periodEnd}`}>
                <IgAccordion
                  title={`${DateTime.fromSeconds(
                    event.periodStart
                  ).toLocaleString(DateTime.DATE_SHORT)}
                          to
                          ${DateTime.fromSeconds(
                            event.periodEnd
                          ).toLocaleString(DateTime.DATE_SHORT)}`}
                  defaultExpanded={index === 0}
                >
                  <Grid container item direction={"column"}>
                    <StripeEventMeterLogTable events={event.events} />
                  </Grid>
                </IgAccordion>
              </Grid>
            ))}
          </Grid>
        </Grid>
      ) : null}
      {data.shopifyEvents.length > 0 ? (
        <>
          <Grid item>
            <IgTypography sx={{ fontWeight: 600 }}>Shopify Events</IgTypography>
          </Grid>
          <Grid item>
            <IgPaper sx={{ padding: "4px 16px" }}>
              <ShopifyChargeTable events={data.shopifyEvents} />
            </IgPaper>
          </Grid>
        </>
      ) : null}
    </Grid>
  );
};

export const MeterLogDrawer = () => {
  const drawer = useSelector(getDrawer("meterLog"));
  const dispatch = useDispatch();

  const handleClose = () => {
    dispatch(closeDrawer("meterLog"));
  };

  if (drawer === undefined || !drawer.subscription?.subscriptionLinkId) {
    return null;
  }

  return (
    <Drawer
      anchor="right"
      open
      onClose={() => handleClose()}
      sx={{
        zIndex: 2000,
        ".MuiDrawer-paper": { borderRadius: "0 !important" },
      }}
    >
      <Grid
        direction={"column"}
        container
        sx={{ width: 800, p: 3 }}
        rowSpacing={3}
      >
        <Grid item sx={{ marginBottom: "20px !important" }}>
          <IgTypography variant="h3">Usage</IgTypography>
        </Grid>
        <Box sx={{ borderTop: 1, borderColor: "divider" }}>
          <Content
            stripeProductLinkId={drawer.subscription.stripeProductLinkId}
            subscriptionLinkId={drawer.subscription.subscriptionLinkId}
            organizationId={drawer.subscription.organizationId}
          />
        </Box>
      </Grid>
    </Drawer>
  );
};
